<div class="container">

    <div class="margin">

        <!--If the user isn't an ASMS owner-->
        <div class="no-asms-banner" *ngIf="noAsms | async">
            <img src="../../assets/icons/alert-circle.svg" />
            <p>
                You need to be the owner of an ASMS.
                Please <a href="https://asms.gm.com/" target="_blank" class="asms-link">create an ASMS</a> before making
                a cloud account.
                Return and refresh the page once completed.
            </p>
        </div>

        <!--first step-->
        <mat-stepper #stepper class="steps" linear="true">

            <mat-step [stepControl]="firstFormGroup">
                <form [formGroup]="firstFormGroup" class="first-form">

                    <ng-template matStepLabel>Account Creation</ng-template>

                    <div class="info">
                        <div class="title-large">Create Azure Cloud Subscription</div>
                        <div class="subtitle">Provide the details to create your Azure subscription.</div>
                        <mat-error *ngIf="firstFormGroup.invalid && firstFormGroup.dirty" class="error">
                            You must fill out all of the required fields to complete this form.
                        </mat-error>
                    </div>

                    <div class="space"></div>

                    <!-- ASMS section -->
                    <div class="form-section" id="account-details">
                        <p class="title-small warning-wrapper">
                            Subscription Details
                            <img *ngIf="noAsms | async" (mouseover)="activateTip()" (mouseout)="deactivateTip()"
                                src="../../assets/icons/alert-circle.svg" />
                            <span class="text-wrapper" *ngIf="(asmsErrorHover | async) && (noAsms | async)">
                                You do not own an ASMS. Return and refresh the page once you've made one.
                            </span>
                        </p>

                        <div class="form-row">
                            <div>
                                <p class="subtitle">
                                    Select the ASMS to associate your Azure subscription to.
                                </p>
                            </div>

                            <div class="form-row-right form-inputs-container">
                                <div class="spinner-container">
                                    <div class="spinner" *ngIf="isAsmsLoading || checkingOpsOwner">
                                        <mat-spinner [diameter]="20" [strokeWidth]="2" />
                                    </div>

                                    <div>
                                        <mat-form-field appearance="outline">
                                            <mat-label>ASMS</mat-label>
                                            <mat-select formControlName="asms" required>
                                                <mat-option *ngFor="let item of asmsList" [value]="item">
                                                    {{ item.asmsId }}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>

                                        <!-- 🔴 Show error if ASMS does not have ops owner -->
                                        <div *ngIf="firstFormGroup.controls['asms'].value && !opsOwnerValid && !checkingOpsOwner"
                                            class="error-message">
                                            ⚠️ The selected ASMS does not have an operations owner. Azure subscription
                                            cannot be created.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="space"></div>

                    <!--env section-->
                    <div class="environment form-section">
                        <p class="title-small">Environment</p>
                        <div class="form-row">

                            <p class="subtitle">
                                Choose the development environment to associate the subscription with.
                            </p>

                            <div class="form-row-right" [ngClass]="{ 'has-spinner': populatingEnv }">
                                <div class="spinner-container">
                                    <div class="spinner" *ngIf="populatingEnv">
                                        <mat-spinner [diameter]="20" [strokeWidth]="2" />
                                    </div>
                                    <div class="form-input">
                                        <mat-form-field appearance="outline">
                                            <mat-label>Environment</mat-label>
                                            <mat-select formControlName="environment">
                                                @for (env of envs; track env) {
                                                <mat-option [value]="env.id">{{ env.name }}</mat-option>
                                                }
                                            </mat-select>
                                        </mat-form-field>
                                    </div>
                                </div>

                                <!-- <p class="asms-occupied" *ngIf="asmsIsOccupied | async">
                                    <img class="icon" src="../../../assets/images/triangle-exclamation-error.svg" />
                                    This ASMS already has accounts for each environment. Please select a different ASMS.
                                </p> -->
                            </div>

                        </div>

                    </div>

                    <div class="space"></div>

                    <!--name section-->
                    <div class="form-section name">

                        <p class="title-small">Name</p>

                        <div class="form-row">
                            <p class="subtitle">
                                Customize the name that is added to a prefix composed of ASMS and environment.
                            </p>

                            <div class="form-row-right">
                                <!-- <span>aASMS - ASMS Acronym - environment - </span> -->
                                <span>{{firstFormGroup.get('asms')?.value?.asmsId || 'aASMS'}} -
                                    {{firstFormGroup.controls.asms.value?.asmsAcronym || 'ASMS Acronym'}} -
                                    {{firstFormGroup.get('environment')?.value || 'environment'}} - </span>
                                <mat-form-field appearance="outline">
                                    <mat-label>Name</mat-label>
                                    <input [disabled]="disableSuffixField()" matInput formControlName="name">
                                </mat-form-field>
                                <mat-error
                                    *ngIf="firstFormGroup.get('name')?.hasError('pattern') && (firstFormGroup.get('name')?.dirty || firstFormGroup.get('name')?.touched)">
                                    Name must be alphanumeric and can contain dashes in between.
                                </mat-error>
                                <mat-error *ngIf="firstFormGroup.get('name')?.hasError('accountNameTooLong')">
                                    Total Account Name must be 40 characters or less.
                                </mat-error>
                            </div>
                        </div>

                    </div>

                    <div class="space"></div>

                    <!--tenant section-->
                    <div class="form-section tenant">

                        <p class="title-small">Azure Tenant</p>

                        <div class="form-row">
                            <p class="subtitle">
                                Select the tenant where this subscription will reside.
                            </p>

                            <div class="form-row-right">
                                <mat-form-field appearance="outline">
                                    <mat-label>GM Public</mat-label>
                                    <mat-select formControlName="tenant" required>
                                        <mat-option *ngFor="let tenant of tenants" [value]="tenant">
                                            {{ tenant.name }}
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </div>
                        </div>

                    </div>

                    <div class="space"></div>

                    <!--management group section-->
                    <div class="form-section management-group">

                        <p class="title-small">Management Group</p>

                        <div class="form-row">
                            <p class="subtitle">
                                Select the appropriate inbound/outbound connectivity for your subscription.
                            </p>

                            <div class="form-row-right">
                                <mat-form-field appearance="outline">
                                    <mat-label>Online</mat-label>
                                    <mat-select formControlName="managementGroup" required>
                                        <mat-option *ngFor="let managementGroup of managementGroups"
                                            [value]="managementGroup">
                                            {{ managementGroup.name }}
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </div>
                        </div>

                    </div>

                    <div class="space"></div>

                    <!--repos section-->
                    <div class="form-section repos">

                        <p class="title-small">GitHub Repos</p>

                        <div class="form-row">
                            <p class="subtitle">
                                Create a comma separated list of all Github repository names for application deployment
                                credentials. Up to 10 repos may be added.
                            </p>

                            <div class="form-row-right textarea-field">
                                <mat-form-field appearance="outline" class="textarea">
                                    <mat-label>Enter repo name(s)</mat-label>
                                    <textarea matInput formControlName="repos"></textarea>
                                    <mat-hint>Expected Repo Format: org name/repo name</mat-hint>
                                </mat-form-field>

                                <mat-error *ngIf="firstFormGroup.get('repos')?.hasError('invalidOrg')">
                                    Invalid organization name. Please make sure to use am approved organization.
                                </mat-error>
                                <mat-error *ngIf="firstFormGroup.get('repos')?.hasError('tooManyRepos')">
                                    You have entered too many repositories.
                                </mat-error>
                                <mat-error *ngIf="firstFormGroup.get('repos')?.hasError('invalidRepoFormat')">
                                    Invalid repository format. Example of accepted format: organization/repository
                                </mat-error>
                            </div>
                        </div>

                    </div>

                    <div class="space"></div>

                    <!--subscription budget section-->
                    <div class="form-section budget">

                        <p class="title-small">Subscription Budget</p>

                        <div class="form-row">
                            <div>
                                <p class="subtitle">
                                    The limit allowed to be spent on the subscription. Login into Azure calculator,
                                    select “Enterprise” for license type and calculate the price.
                                </p>

                                <a mat-button class="link" href="https://azure.microsoft.com/en-ca/pricing/calculator/"
                                    target="_blank" rel="noopener noreferrer">
                                    <div class="button">
                                        Pricing Breakdown
                                        <div class="button-image">
                                            <img src="../../assets/images/external-link.svg" />
                                        </div>
                                    </div>
                                </a>
                            </div>

                            <div class="form-row-right">
                                <mat-form-field appearance="outline">
                                    <mat-label>Budget</mat-label>
                                    <input matInput formControlName="subBudget">
                                </mat-form-field>
                                <mat-error
                                    *ngIf="firstFormGroup.get('subBudget')?.invalid && (firstFormGroup.get('subBudget')?.dirty || firstFormGroup.get('subBudget')?.touched)">
                                    Please use a format like 300 or 300.00. Value must be greater than 0.
                                </mat-error>
                            </div>


                        </div>

                    </div>
                </form>

                <div class="buttons-right">
                    <button mat-flat-button class="container" matStepperNext (click)="cancel()">
                        Cancel
                    </button>
                    <button mat-flat-button matStepperNext
                        [disabled]="firstFormGroup.invalid || checkingAccountName || (noAsms | async) || !opsOwnerValid"
                        [ngClass]=" firstFormGroup.invalid || checkingAccountName || (noAsms | async) || !opsOwnerValid ? 'disabled-button' : 'button-blue'">
                        <!-- TODO: Put this back to the check -->
                        <!-- || !accountNameValid -->
                        Next
                    </button>
                </div>
            </mat-step>

            <!--second step-->
            <mat-step [stepControl]="secondFormGroup">
                <form>
                    <ng-template matStepLabel>Confirmation</ng-template>

                    <div class="info">
                        <div class="title-large">Create Azure Cloud Account</div>
                        <div class="subtitle">Review the details to create your Azure subscription.</div>
                    </div>

                    <div class="space"></div>

                    <!--ASMS section-->
                    <div class="form-section form-review asms">
                        <p class="title-small">Subscription Details</p>
                        <div class="form-row">
                            <p class="subtitle">
                                Select the ASMS to associate your Azure subscription to.
                            </p>
                            <div class="form-row-right form-inputs-container asms-review">
                                <div class="subtitle">{{ firstFormGroup.get('asms')?.value?.asmsId }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="space"></div>

                    <!--env section-->
                    <div class="form-section form-review environment">
                        <p class="title-small">Environment</p>
                        <div class="form-row">
                            <div class="subtitle">
                                Choose the development environment to associate the subscription with.
                            </div>
                            <div class="form-row-right form-inputs-container">
                                <div class="subtitle">{{ firstFormGroup.get('environment')?.value }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="space"></div>

                    <!--name section-->
                    <div class="form-section form-review name">
                        <p class="title-small">Name</p>
                        <div class="form-row">
                            <div class="subtitle">
                                Customize the name that is added to a prefix composed of ASMS and environment.
                            </div>
                            <div class="form-row-right form-inputs-container">
                                <div class="subtitle">
                                    {{ firstFormGroup.get('name')?.value }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="space"></div>

                    <!--tenant section-->
                    <div class="form-section tenant">
                        <div class="title-small">Azure Tenant</div>
                        <div class="form-row">
                            <div class="subtitle">
                                Select the tenant where this subscription will reside.
                            </div>
                            <div class="subtitle form-row-right">
                                {{ firstFormGroup.get('tenant')?.value?.name }}
                            </div>
                        </div>
                    </div>

                    <div class="space"></div>

                    <!--management group section-->
                    <div class="form-section management">
                        <div class="title-small">Management Group</div>
                        <div class="form-row">
                            <div class="subtitle">
                                Select the appropriate inbound/outbound connectivity for your subscription.
                            </div>
                            <div class="subtitle form-row-right">
                                {{ firstFormGroup.get("managementGroup")?.value?.name }}
                            </div>
                        </div>

                    </div>

                    <div class="space"></div>

                    <!--repos section-->
                    <div class="form-section form-review github">
                        <div class="title-small">
                            GitHub Repos
                        </div>

                        <div class="form-row">
                            <div class="subtitle">
                                Create a comma separated list of all Github repository names for application deployment
                                credentials. Up to 10 repos may be added.
                            </div>

                            <div class="subtitle form-row-right">
                                <ul *ngFor="let repo of getRepoList()" style="padding-left: 0;">
                                    <li>{{repo}}</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div class="space"></div>

                    <!--budget section-->
                    <div class="form-section form-review budget">
                        <div class="title-small">Subscription Budget</div>

                        <div class="form-row">
                            <div class="subtitle">
                                The limit allowed to be spent on the subscription.
                            </div>

                            <div class="subtitle form-row-right">
                                {{firstFormGroup.get("subBudget")?.value}}
                            </div>
                        </div>

                    </div>

                    <div class="space"></div>


                    <div class="button-container">
                        <div class="button-left">
                            <button (click)="cancel()" mat-button>Cancel</button>
                        </div>
                        <div class="button-right">
                            <button mat-button matStepperPrevious>Previous</button>
                            <button [disabled]="!opsOwnerValid" (click)="submit()" class="submit-btn" mat-flat-button matStepperNext>Submit</button>
                            <!-- <button class="submit-btn" mat-flat-button matStepperNext>Submit</button> -->

                        </div>
                    </div>

                </form>
            </mat-step>
        </mat-stepper>





    </div>

</div>